import { ArticleSerialDto } from './../../../../../../../../goliath-products/proxy/src/proxy/digiwall/goliath-products/articles/article-serials/models';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  Injector,
  Input,
  input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  ICellRendererParams,
  IRowNode,
  MenuItemDef,
  RowDataUpdatedEvent,
} from '@ag-grid-community/core';
import { Subject } from 'rxjs';
import { BaseThirdPartyDocLineEditor, LinesEditorComponent } from '../../../../../components';
import {
  CustomerOrderLineToPrepareDto,
  CustomerPreparationDto,
  CustomerPreparationService,
  DocLineRole,
  DocLineUpdatedDto,
} from '@digiwall/goliath-documents/proxy';
import { StorageLocationLookupDto, VatType } from '@digiwall/goliath-core/proxy';
import { NewGuid, ObserverQueue } from '@digiwall/core';
import { DocType } from '../../../../base-docs/detail/doc-type';
import { StockFeatureDefinition } from '@digiwall/goliath-products/config';
import { FieldTypes, IHtmlCellRendererParams } from '@digiwall/ag-grid';
import { ColorizedIcon, ColorizedIconHelper } from '../../../../model/colorized-icon.model';
import { CurrentCompanyStore } from '@digiwall/goliath-core';

import { ArticleRangeValueStore } from '@digiwall/goliath-products';
import { ColumnDefinitionsService } from 'projects/goliath-documents/src/lib/services/column-definitions.service';
import { ArticleLotDto, ArticleRangeValueDto } from '@digiwall/goliath-products/proxy';
import { ToasterService } from '@abp/ng.theme.shared';

@Component({
  selector: 'document-customer-preparation-lines-editor',
  templateUrl: './customer-preparation-lines-editor.component.html',
  styleUrl: './customer-preparation-lines-editor.component.scss',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export class CustomerPreparationLinesEditorComponent implements AfterViewInit, OnDestroy {
  protected readonly stockFeature = inject(StockFeatureDefinition);
  protected readonly currentCompanyStore = inject(CurrentCompanyStore);
  protected readonly columnDefinitionsService = inject(ColumnDefinitionsService);
  protected readonly articleRangeValueStore = inject(ArticleRangeValueStore);

  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;
  @Input() set lines(value: any[]) {
    const incomingLines: CustomerOrderLineToPrepareDto[] = value?.filter(x => !x.isDeposit) ?? [];

    if (!this._lines?.length || !this.lineGrid?.grid?.grid?.api) {
      this._lines = incomingLines;
      this.processLines();
      return;
    }

    this.updateLinesIncrementally(incomingLines);
  }
  _lines: CustomerOrderLineToPrepareDto[];
  public get lines() {
    return this._lines;
  }
  private processedLineIds = new Set<string>();
  private isRestoringSelection = false;

  private isCellEditable = (params: EditableCallbackParams<CustomerOrderLineToPrepareDto, any>) =>
    // Editable if article line AND it is NOT a reservation for a stock managed article (if not reservation OR not stock managed, you can edit)
    !(params.data?.isFromReservation && params.data?.isStockManaged && params.data?.isService) &&
    params.data?.lineRole == DocLineRole.SalesPurchaseArticle;
  public actionBtns: any[] = [];
  private destroy$ = new Subject<void>();
  protected articleLineRole: DocLineRole = DocLineRole.SalesPurchaseArticle;
  protected textLineRole: DocLineRole = DocLineRole.CustomerDocText;
  protected chapterLineRole: DocLineRole = DocLineRole.SalesPurchaseChapter;
  private groupColdDef = {
    field: 'orderDisplayNumber',
    headerName: 'Commande',
    rowGroup: true,
    hide: true,
  };
  protected columnDefs: ColDef[] = [
    this.groupColdDef,
    {
      field: 'displayReference',
      headerName: '#',
      type: FieldTypes.String,
      width: 70,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      sort: 'asc',
      pinned: 'left',
      suppressMovable: true,
      suppressAutoSize: true,
    },
    {
      field: '_metaType',
      headerName: '',
      width: 120,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      suppressAutoSize: true,
      resizable: false,
      cellRenderer: (
        params: ICellRendererParams<CustomerOrderLineToPrepareDto & { bottomRow?: boolean }>,
      ) => {
        if (params.data?.bottomRow) return '';
        let stockStr = '';
        let typeStr = '';
        // if (params.data?.isStockManaged === true) {
        //   // Show 'is stock managed' article line if no parent, or chapter line, or in other words only on parent lines
        //   stockStr = `
        //     <span class="badge rounded-pill" style="color:var(--bs-success); background-color: rgba(var(--bs-success-rgb), 0.1);" title="${this.localizationService.instant('Product::Article:StockManaged')}">
        //       <i class="digi-out-stock-inventories"></i>
        //     </span>`;
        // }
        if (params.data?.lineRole == DocLineRole.SalesPurchaseArticle) {
          // Article line (not chapter), show backorder/reservation tag instead
          let color = 'info';
          let name = 'Reservation';
          let icon = 'digi-out-edit-dialog';
          if (!params.data?.isFromReservation) {
            color = 'warning';
            name = 'Backorder';
            icon = 'digi-out-customer-order';
          }
          typeStr = `
            <span class="badge rounded-pill" style="color:var(--bs-${color}); background-color: rgba(var(--bs-${color}-rgb), 0.1);">
              <i class="${icon} me-1"></i>${this.localizationService.instant('Doc::Doc:' + name)}
            </span>`;
        }
        return stockStr + typeStr;
      },
      // Span the label cell across every column between the badge and the two total
      // columns, so the pinned total row still renders its own weight and price. Derived
      // rather than hardcoded because this grid persists the user's column layout.
      colSpan: params => {
        if (!params.data?.bottomRow) return 1;
        const colIds = params.api.getAllDisplayedColumns().map(column => column.getColId());
        const from = colIds.indexOf('_metaType');
        const to = colIds.indexOf('totalWeight');
        return from >= 0 && to > from ? to - from : 1;
      },
    },
    {
      field: 'code',
      headerName: this.localizationService.instant('Doc::CustomerReference'),
    },
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
    },
    {
      field: 'articleSalesUnitCode',
      headerName: this.localizationService.instant('Product::Article:ProductSaleUnit'),
    },
    {
      field: 'selectedQuantity',
      editable: params =>
        params.data.lineRole == DocLineRole.SalesPurchaseArticle &&
        params.node.isSelected() &&
        params.data.remainingQuantity > 0,
      headerName: this.localizationService.instant('Doc::Preparation:QuantityToPrepare'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.quantityDecimals,
      },
      filter: false,
    },
    {
      field: 'preparedQuantity',
      headerName: this.localizationService.instant('Doc::Preparation:AlreadyPreparedQuantity'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.quantityDecimals,
      },
      filter: false,
    },
    {
      field: 'remainingQuantity',
      headerName: this.localizationService.instant('Doc::Preparation:RemainingQuantity'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.quantityDecimals,
      },
      filter: false,
    },
    this.columnDefinitionsService.getStockLocationColDef({
      isCellEditable: this.isCellEditable,
      setLineStockageLocation: (
        lineId: string,
        stockLocationId: string,
        storage: StorageLocationLookupDto,
      ) => {
        const line = this._lines.find(x => x.id == lineId);
        if (line) {
          line.stockLocationId = stockLocationId;
          line.stockLocation = storage.code;
        }
      },
      getDocument: () => null,
      // withStockOnly: (item: CustomerOrderLineToPrepareDto) => item.selectedQuantityToDeliver > 0,
      withQuantity: DocType.Sales,
    }),
    this.columnDefinitionsService.getArticleLotColDef({
      isCellEditable: this.isCellEditable,
      setArticleLot: (lineId: string, articleLotId: string, articleLot: ArticleLotDto) => {
        const line = this._lines.find(x => x.id == lineId);
        if (line) {
          line.articleLotId = articleLotId;
          line.articleLot = articleLot.code;
        }
      },
      articleLotFormatter: this.articleLotFormatter.bind(this),
    }),
    this.columnDefinitionsService.getArticleRange1ColDef(),
    this.columnDefinitionsService.getArticleRangeValue1ColDef({
      isCellEditable: this.isCellEditable,
      setArticleRangeValue1: (lineId: string, valueId: string, value: ArticleRangeValueDto) => {
        const line = this._lines.find(x => x.id == lineId);
        if (line) {
          line.articleRangeValue1Id = valueId;
          line.articleRangeValue1 = value.name.current;
        }
      },
    }),
    this.columnDefinitionsService.getArticleRange2ColDef(),
    this.columnDefinitionsService.getArticleRangeValue2ColDef({
      isCellEditable: this.isCellEditable,
      setArticleRangeValue2: (lineId: string, valueId: string, value: ArticleRangeValueDto) => {
        const line = this._lines.find(x => x.id == lineId);
        if (line) {
          line.articleRangeValue2Id = valueId;
          line.articleRangeValue2 = value.name.current;
        }
      },
    }),
    this.columnDefinitionsService.getArticleSerialColDef({
      isCellEditable: this.isCellEditable,
      setArticleSerial: (lineId: string, articleSerialId: string, serial: ArticleSerialDto) => {
        const line = this._lines.find(x => x.id == lineId);
        if (line) {
          line.articleSerialId = articleSerialId;
          line.articleSerial = serial.serialNumber;
        }
      },
      articleSerialFormatter: this.articleSerialFormatter.bind(this),
    }),
    {
      field: 'totalWeight',
      headerName: this.localizationService.instant('::TotalWeight'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.weightDecimals,
      },
      filter: false,
    },
    {
      field: 'totalPrice',
      headerName: this.localizationService.instant('::TotalPrice'),
      type: FieldTypes.Currency,
      filter: false,
    },
  ];

  private readonly totalRow = {
    id: NewGuid,
    bottomRow: true,
    isBottom: true,
    selectedQuantity: 0,
    totalWeight: 0,
    totalPrice: 0,
  };
  protected readonly pinnedBottomRowData: any[] = [this.totalRow];

  // Keeps LinesEditorComponent's built-in rules (chapter/text lines) but exempts the pinned
  // total row from 'ag-row-disabled', whose !important grey would hide the footer styling.
  protected readonly rowClassRules = {
    'footer-row': params => params.data?.bottomRow === true,
    'ag-row-disabled': params =>
      params.data != null && !params.data.bottomRow && !params.data.isEditable,
  };

  constructor(
    private localizationService: LocalizationService,
    private toasterService: ToasterService,
  ) {
    this.initColDefs();
  }
  private processLines(): void {
    if (!this._lines?.length) return;

    this._lines.forEach(line => {
      if (!this.processedLineIds.has(line.id)) {
        if (line.lineRole === DocLineRole.CustomerDocText) {
          line.selectedQuantity = 1;
        } else if (line.toPrepareQuantity > 0 && (line.isFromReservation || !line.isStockManaged)) {
          line.selectedQuantity = line.toPrepareQuantity;
        }
        this.processedLineIds.add(line.id);
      }
      this.recomputeLineTotals(line);
    });
    this.computeTotalRow();

    this.lineGrid.grid.grid.api.setRowGroupColumns(['orderDisplayNumber', 'displayReference']);
    setTimeout(() => {
      this.lineGrid.grid.grid.api.expandAll();
      this.lineGrid.grid.grid.api.autoSizeAllColumns();
    }, 0);
  }
  private updateLinesIncrementally(incomingLines: CustomerOrderLineToPrepareDto[]): void {
    const incomingIds = new Set(incomingLines.map(l => l.id));
    const currentIds = new Set(this._lines.map(l => l.id));

    const linesToAdd = incomingLines.filter(l => !currentIds.has(l.id));
    const linesToRemove = this._lines.filter(l => !incomingIds.has(l.id));
    const removeIds = new Set(linesToRemove.map(l => l.id));

    linesToAdd.forEach(line => {
      if (!this.processedLineIds.has(line.id)) {
        if (line.lineRole === DocLineRole.CustomerDocText) {
          line.selectedQuantity = 1;
        } else if (line.toPrepareQuantity > 0 && (line.isFromReservation || !line.isStockManaged)) {
          line.selectedQuantity = line.toPrepareQuantity;
        }
      }
      this.processedLineIds.add(line.id);
      this.recomputeLineTotals(line);
    });

    // Mutate in-place to keep same array reference and avoid triggering LinesEditorComponent.rows setter
    for (let i = this._lines.length - 1; i >= 0; i--) {
      if (removeIds.has(this._lines[i].id)) {
        this._lines.splice(i, 1);
      }
    }
    this._lines.push(...linesToAdd);

    const api = this.lineGrid.grid.grid.api;
    if (linesToRemove.length || linesToAdd.length) {
      api.applyTransaction({
        add: linesToAdd.length ? linesToAdd : undefined,
        remove: linesToRemove.length ? linesToRemove : undefined,
      });
      this.computeTotalRow();
    }

    if (linesToAdd.length) {
      setTimeout(() => {
        this.isRestoringSelection = true;
        api.forEachNode(node => {
          if (node.data && linesToAdd.some(l => l.id === node.data.id) && node.data.selectedQuantity > 0) {
            node.setSelected(true);
          }
        });
        this.isRestoringSelection = false;

        api.setRowGroupColumns(['orderDisplayNumber', 'displayReference']);
        api.expandAll();
        api.autoSizeAllColumns();
      }, 0);
    }
  }
  private articleSerialFormatter(data: any, articleSerial: any): string {
    if (data == null || articleSerial == null) return '';
    if (typeof articleSerial === 'string')
      return articleSerial != 'articleSerial' ? articleSerial : '';
    return articleSerial.serialNumber ?? '';
  }
  private articleLotFormatter(data: any, articleLot: any): string {
    if (data == null || articleLot == null) return '';
    if (typeof articleLot === 'string') return articleLot != 'articleLot' ? articleLot : '';
    return articleLot.code ?? '';
  }
  protected onCellValueChanged = (
    event: CellValueChangedEvent<CustomerOrderLineToPrepareDto, any>,
  ) => {
    if (event.column.getColId() == 'selectedQuantity') {
      if (event.data.selectedQuantity > event.data.remainingQuantity) {
        this.toasterService.error('Doc::DeliveryNote:ExceededQuantityForPreparation');
        event.data.selectedQuantity = event.oldValue;
      }
      if (event.data.selectedQuantity == 0) event.node.setSelected(false);

      this.recomputeLineTotals(event.data);
      this.computeTotalRow();
      this.lineGrid.grid.grid.api.applyTransaction({ update: [event.data] });
    }
  };
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.processedLineIds.clear();
  }
  ngAfterViewInit(): void {
    this.lineGrid.grid.grid.api.setGridOption('selectionColumnDef', {
      pinned: 'left',
      lockPinned: true,
    });
    this.lineGrid.grid.grid.api.setGridOption('rowGroupPanelShow', 'never');
    this.lineGrid.grid.grid.api.setGridOption('rowSelection', {
      mode: 'multiRow',
      groupSelects: 'descendants',
      isRowSelectable: params =>
        !!params.data &&
        ((params.data.lineRole == DocLineRole.SalesPurchaseArticle && params.data.remainingQuantity > 0) ||
          params.data.lineRole == DocLineRole.CustomerDocText),
      enableSelectionWithoutKeys: true,
      hideDisabledCheckboxes: false,
    });
    this.lineGrid.grid.grid.api.setGridOption('groupRowRendererParams', {
      suppressCount: true,
    });
    this.initializeDefaultSelection();

    this.lineGrid.grid.grid.api.setGridOption('onRowSelected', event => {
      if (event.data == null) return;
      if (this.isRestoringSelection) return;

      let line = event.data as CustomerOrderLineToPrepareDto;

      if (line.lineRole === DocLineRole.CustomerDocText) {
        line.selectedQuantity = event.node.isSelected() ? 1 : 0;
      } else {
        line.selectedQuantity = event.node.isSelected() ? line.toPrepareQuantity : 0;
      }

      this.recomputeLineTotals(line);
      this.computeTotalRow();

      this.lineGrid.grid.grid.api.applyTransaction({ update: [line] });
    });
  }

  /**
   * Total weight and price are derived client-side from the quantity being prepared, the same
   * way the delivery-note creation grid derives them for non-article lines. They are
   * indicative: the document's rounding service and piece-dimension conversion are not applied.
   */
  private recomputeLineTotals(
    line: CustomerOrderLineToPrepareDto & { totalWeight?: number; totalPrice?: number },
  ): void {
    if (line?.articleId == null) return;

    const quantity = line.selectedQuantity ?? 0;
    line.totalPrice = (line.unitPrice ?? 0) * quantity * (1 - (line.discount ?? 0) / 100);
    line.totalWeight = (line.unitWeight ?? 0) * quantity;
  }

  private computeTotalRow(): void {
    const articleLines: any[] = this._lines?.filter(line => line.articleId != null) ?? [];

    this.totalRow.selectedQuantity = articleLines.reduce(
      (total, line) => total + (line.selectedQuantity ?? 0),
      0,
    );
    this.totalRow.totalWeight = articleLines.reduce(
      (total, line) => total + (line.totalWeight ?? 0),
      0,
    );
    this.totalRow.totalPrice = articleLines.reduce(
      (total, line) => total + (line.totalPrice ?? 0),
      0,
    );

    const bottomRowNode = this.lineGrid?.grid?.grid?.api?.getPinnedBottomRow(0);
    if (bottomRowNode) {
      this.lineGrid.grid.grid.api.redrawRows({ rowNodes: [bottomRowNode] });
    }
  }
  private initializeDefaultSelection() {
    this.lineGrid.grid.grid.api.setGridOption(
      'onRowDataUpdated',
      (params: RowDataUpdatedEvent<CustomerOrderLineToPrepareDto>) => {
        const nodesToSelect: IRowNode[] = [];
        params.api.forEachNode(node => {
          if (node.data?.selectedQuantity > 0) {
            nodesToSelect.push(node);
          }
        });
        params.api.setNodesSelected({ nodes: nodesToSelect, newValue: true });
      },
    );
  }

  private initColDefs(): void {}

  isChapter = (line: { lineRole?: DocLineRole }): boolean =>
    line.lineRole == DocLineRole.SalesPurchaseChapter;

  getContextMenuItems = _ => [];
}
